import i18next from 'i18next';
import {
  all,
  takeEvery,
  call,
  put,
  select,
  take,
  race,
  delay,
} from 'redux-saga/effects';
import { BiData, StudioBatchUpdateBiEvent } from '@wix/da-bi/pkg/events';
import {
  PapiStudioBatchEditAttribute,
  PapiStudioBatchEditDescription,
  PapiStudioBatchEditTags,
  PapiStudioBatchEditTitle,
  PapiStudioBatchEditgallery,
  PapiStudioBatchEditsubjecttags,
  PapiStudioBatchEditLicense,
  PapiStudioBatchInitGalleries,
  PapiStudioBatchInitTiers,
  PapiStudioBatchApplyPreset,
  PapiDaSharedGallectionFoldersMoveDeviations,
  PapiDeviation,
  PapiError,
} from '@wix/da-papi-types';
import { getDeviationType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import { getDataEntitiesByIds } from '@wix/da-shared-react/pkg/redux/entities/selectors';
import {
  getCurrentUserHasCore,
  getIsMobile,
} from '@wix/da-shared-react/pkg/publicSession/selectors';
import { deviationSchema } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/deviation';
import { presetsLoadGalleriesSuccess } from '@wix/da-submit-shared/pkg/redux/actions';
import {
  pushGlobalConfirmationModal,
  confirmYes,
  confirmNo,
  confirmValue,
  pushModal,
  popModal,
} from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import { requestPuppy, requestNapi, isApiError } from '@wix/da-http-client';
import {
  addNotification,
  addErrorNotification,
} from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { NotificationDuration } from '@wix/da-shared-react/pkg/Notifications/Notification';
import { batchDeviationUpdateComplete } from '@wix/da-shared-react/pkg/redux/deviations/actionCreators';
import { getStream } from '@wix/da-shared-react/pkg/Stream/base/selectors';
import type { OffsetStream } from '@wix/da-shared-react/pkg/Stream/withOffset/types';
import { Alert as AlertIllustration } from '@wix/da-ds/pkg/Icons/Confirmations/Alert';
import { submitAdapter as labelsAdapter } from '@wix/da-submit-shared/pkg/fields/LabelsField/adapters';
import { submitAdapter as licenseAdapter } from '@wix/da-submit-shared/pkg/fields/LicenseField/adapters';
import {
  MAX_ATTACHMENTS,
  MAX_ATTACHMENTS_CORE,
} from '@wix/da-submit-shared/pkg/constants';
import {
  batchEditTitle,
  batchEditTags,
  batchEditDescription,
  batchEditGallery,
  batchEditTier,
  batchEditLabels,
  batchEditLicense,
  batchSchedule,
  batchEditAttribute,
  batchEditPreset,
  batchPublish,
  batchUnpublish,
  batchDoDelete,
  batchStart,
  batchFinish,
  batchError,
  batchLoadGalleries,
  batchLoadGalleriesFailure,
  batchLoadGalleriesSuccess,
  batchLoadTiers,
  batchLoadTiersFailure,
  batchLoadTiersSuccess,
  batchMerge,
  batchMergeSuccess,
  batchMergeFailure,
  batchMoveToStash,
  batchMoveToDrafts,
  refreshTable,
  refreshTableSuccess,
  batchEditDisplayOptions,
} from '../../actions/studio';
import {
  getResultsTotalCount,
  getRunningBatchById,
  getSelectedDeviationIds,
  getSelectedFolder,
  getFeaturedFolderId,
  getSavedSubmissionsFolderId,
} from '../../selectors/studio';
import { NotificationType } from '../../types/notifications';
import { BatchType, RunningBatch } from '../../types/batchActions';
import {
  STUDIO_DEVIATIONS_STREAM,
  BATCH_SCHEDULE_ERROR_MAXED_OUT,
  BATCH_SCHEDULE_ERROR_TIMESLOT_FULL,
  MULTIIMAGE_ALLOWED_FILETYPES,
} from '../../../constants';
import DeviationMediaService from '@wix/da-shared-react/pkg/utils/DeviationMediaService';
import { UploadErrorCode } from '@wix/da-shared-react/pkg/redux/uploads/types';
import { BATCH_TYPES_THAT_KEEP_SELECTION } from './constants';

type BatchResponseType =
  | PapiStudioBatchEditTitle
  | PapiStudioBatchEditTags
  | PapiStudioBatchEditDescription
  | PapiStudioBatchEditLicense
  | PapiStudioBatchEditsubjecttags
  | PapiStudioBatchEditAttribute
  | PapiStudioBatchEditgallery
  | PapiStudioBatchApplyPreset;

function* handleBatchAction(
  name: BatchType,
  url: string,
  payload: any,
  errorI18nKey?: string,
  isPuppy = true,
  papiBase = 'studio'
) {
  const selectedDrafts = yield select(getSelectedDeviationIds);
  const data = {
    deviationids: selectedDrafts,
    ...payload,
  };
  const response: BatchResponseType = yield call(
    isPuppy ? requestPuppy : requestNapi,
    {
      method: 'post',
      url,
      data,
    },
    undefined,
    !isPuppy ? undefined : papiBase
  );
  const errorMessage = errorI18nKey
    ? i18next.t(errorI18nKey)
    : i18next.t('studio.notifications.batchError.generic.text');

  if (!response || isApiError(response)) {
    yield put(
      batchError(
        name,
        errorMessage,
        (response as unknown as PapiError)?.errorCode ?? null
      )
    );
    return;
  }
  if (!(response.success && response.batchId)) {
    yield put(batchError(name, errorMessage, null));
    return;
  }
  yield put(
    batchStart(
      name,
      response.batchId,
      Object.keys(selectedDrafts).map(parseInt)
    )
  );
}

function* handleBatchEditTitle({
  payload: { operation, title },
}: ReturnType<typeof batchEditTitle>) {
  yield call(handleBatchAction, BatchType.TITLE, 'batch/edit_title', {
    operation,
    title,
  });
}

function* handleBatchEditTags({
  payload: { operation, tags },
}: ReturnType<typeof batchEditTags>) {
  yield call(handleBatchAction, BatchType.TAGS, 'batch/edit_tags', {
    operation,
    tags: (tags || []).filter(tag => !!tag),
  });
}

function* handleBatchEditDescription({
  payload: { newState },
}: ReturnType<typeof batchEditDescription>) {
  const editorPapi = { editorRaw: JSON.stringify(newState) };

  // node api urls are built differently than puppy so we have to add the studio/ prefix
  yield call(
    handleBatchAction,
    BatchType.DESCRIPTION,
    'studio/batch/edit_description',
    editorPapi,
    undefined,
    false // napi
  );
}

function* handleBatchEditGallery({
  payload: { targetFolderId },
}: ReturnType<typeof batchEditGallery>) {
  yield call(handleBatchAction, BatchType.GALLERY, 'batch/editgallery', {
    target_folderid: targetFolderId,
  });
}

function* handleBatchEditTier({
  payload: { tierIds },
}: ReturnType<typeof batchEditTier>) {
  yield call(handleBatchAction, BatchType.TIER, 'batch/edittier', {
    tierid: tierIds,
  });
}

function* handleBatchEditLabels({
  payload: { labels },
}: ReturnType<typeof batchEditLabels>) {
  const subjectTags = yield call(labelsAdapter, { labels });
  yield call(
    handleBatchAction,
    BatchType.LABELS,
    'batch/editsubjecttags',
    subjectTags
  );
}

function* handleBatchEditLicense({
  payload: { license },
}: ReturnType<typeof batchEditLicense>) {
  const licenseOptions = yield call(licenseAdapter, { license });
  yield call(
    handleBatchAction,
    BatchType.LICENSE,
    'batch/edit_license',
    licenseOptions
  );
}

function* handleBatchEditAttribute({
  payload: { attrName, attrValue },
}: ReturnType<typeof batchEditAttribute>) {
  yield call(handleBatchAction, BatchType.ATTRIBUTE, 'batch/edit_attribute', {
    attr_name: attrName,
    attr_value: attrValue,
  });
}

function* handleBatchEditDisplayOptions({
  payload: { displayResolution, addWatermark },
}: ReturnType<typeof batchEditDisplayOptions>) {
  yield call(
    handleBatchAction,
    BatchType.DISPLAY_OPTIONS,
    'batch/edit_display_options',
    {
      display_resolution: displayResolution,
      add_watermark: addWatermark,
    }
  );
}

function* handleBatchEditPreset({
  payload: { presetId },
}: ReturnType<typeof batchEditPreset>) {
  yield call(handleBatchAction, BatchType.TIER, 'batch/apply_preset', {
    presetid: presetId,
  });
}

function* handleBatchPublish() {
  yield call(handleBatchAction, BatchType.PUBLISH, 'batch/publish', {});
}

function* handleBatchUnpublish() {
  yield call(handleBatchAction, BatchType.UNPUBLISH, 'batch/unpublish', {});
}

function* handleBatchSchedule({
  payload: { scheduleTs },
}: ReturnType<typeof batchSchedule>) {
  yield call(handleBatchAction, BatchType.SCHEDULE, 'batch/schedule', {
    scheduled_time: scheduleTs,
  });
}

function* handleBatchDoDelete() {
  yield call(handleBatchAction, BatchType.DELETE, 'batch/delete', {});
}

function* handleBatchDeviationUpdateComplete({
  payload,
}: ReturnType<typeof batchDeviationUpdateComplete>) {
  // TS seems to think batchId is a number, but it's a string in runtime...
  const batchId = parseInt(`${payload.batchId}`);
  const runningBatch: RunningBatch = yield select(getRunningBatchById, batchId);
  const { processedCount, failedCount } = payload;

  yield put(batchFinish(batchId, runningBatch, processedCount, failedCount));
}

function* closeModalIfMobile() {
  const isMobile = yield select(getIsMobile);
  if (isMobile) {
    yield put(popModal());
  }
}

function* handleBatchError({
  payload: { batchType, message, errorCode },
}: ReturnType<typeof batchError>) {
  // we have a specific UI for the "schedule limit reached" error
  if (
    batchType === BatchType.SCHEDULE &&
    errorCode &&
    [
      BATCH_SCHEDULE_ERROR_MAXED_OUT,
      BATCH_SCHEDULE_ERROR_TIMESLOT_FULL,
    ].includes(errorCode)
  ) {
    yield put(pushModal(ModalType.DEVIATION_SUBMIT_SCHEDULE_LIMIT_REACHED, {}));
    return;
  }

  yield put(addErrorNotification(message, NotificationDuration.S));
}

function* handleBatchLoadTiers() {
  const response: PapiStudioBatchInitTiers = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'batch/init/tiers',
    },
    undefined,
    'studio'
  );

  if (!response || isApiError(response)) {
    yield put(
      addErrorNotification(
        i18next.t('studio.notifications.batchError.generic.text'),
        NotificationDuration.S
      )
    );
    yield put(batchLoadTiersFailure());
    return;
  }
  yield put(batchLoadTiersSuccess(response.tiers));
}

function* handleBatchLoadGalleries() {
  const response: PapiStudioBatchInitGalleries = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'batch/init/galleries',
    },
    undefined,
    'studio'
  );

  if (!response || isApiError(response)) {
    yield put(
      addErrorNotification(
        i18next.t('studio.notifications.batchError.generic.text'),
        NotificationDuration.S
      )
    );
    yield put(batchLoadGalleriesFailure());
    return;
  }
  yield put(batchLoadGalleriesSuccess(response.galleries));
  yield put(presetsLoadGalleriesSuccess(response.galleries));
}

function* handleBatchMoveToStash() {
  yield put(
    pushGlobalConfirmationModal({
      titleTransKey: 'studio.modals.moveToStash.title',
      textTransKey: 'studio.modals.moveToStash.text',
      confirmBtnLabelTransKey: 'studio.modals.moveToStash.confirmBtn',
      withCancelButton: true,
    })
  );

  const { no } = yield race({
    yes: take(confirmYes),
    no: take(confirmNo),
  });

  if (no) {
    return;
  }

  const deviationIds = yield select(getSelectedDeviationIds);
  const srcFolder = yield select(getSelectedFolder);
  const destFolderId = yield select(getFeaturedFolderId);

  const response: PapiDaSharedGallectionFoldersMoveDeviations = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'gallection/folders/move_deviations',
      data: {
        type: 'stash',
        src_folderid: srcFolder.folderId,
        target_folderid: destFolderId,
        deviationids: deviationIds,
      },
    }
  );

  if (!response || isApiError(response)) {
    yield put(
      addErrorNotification(
        i18next.t('studio.notifications.batchError.generic.text'),
        NotificationDuration.S
      )
    );
    return;
  }

  yield put(refreshTable({ preservePage: true }));
}

function* handleBatchMoveToDrafts() {
  yield put(
    pushGlobalConfirmationModal({
      titleTransKey: 'studio.modals.moveToDrafts.title',
      textTransKey: 'studio.modals.moveToDrafts.text',
      confirmBtnLabelTransKey: 'studio.modals.moveToDrafts.confirmBtn',
      withCancelButton: true,
    })
  );

  const { no } = yield race({
    yes: take(confirmYes),
    no: take(confirmNo),
  });

  if (no) {
    return;
  }

  const deviationIds = yield select(getSelectedDeviationIds);
  const srcFolder = yield select(getSelectedFolder);
  const destFolderId = yield select(getSavedSubmissionsFolderId);

  const response: PapiDaSharedGallectionFoldersMoveDeviations = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'gallection/folders/move_deviations',
      data: {
        type: 'stash',
        src_folderid: srcFolder.folderId,
        target_folderid: destFolderId,
        deviationids: deviationIds,
      },
    }
  );

  if (!response || isApiError(response)) {
    yield put(
      addErrorNotification(
        i18next.t('studio.notifications.batchError.generic.text'),
        NotificationDuration.S
      )
    );
    return;
  }

  yield put(refreshTable({ preservePage: true }));
}

function* handleBatchFinish({
  payload: { batch, failedCount, processedCount },
}: ReturnType<typeof batchFinish>) {
  // If all items failed, throw an error
  if (failedCount > 0 && failedCount === processedCount) {
    yield put(
      batchError(
        batch?.batchType,
        i18next.t('studio.notifications.batchError.generic.text'),
        null
      )
    );
    return;
  }

  // TODO: have a better way of updating the table that doesn't disrupt whatever action the user might be doing
  const stream: OffsetStream = yield select(
    getStream,
    STUDIO_DEVIATIONS_STREAM.streamId
  );
  const resultsTotalCount = yield select(getResultsTotalCount);
  const currentOffset =
    // Checking both prevOffset and hasLess here because prevOffset is a bit sticky. Navigating away from the first page
    // then back to the first page does not reset prevOffset to undefined. Still checking it here for typescript reasons,
    // though.
    stream.prevOffset === undefined || !stream.hasLess
      ? 0
      : stream.prevOffset + stream.itemsPerFetch;
  const itemsLeftFromCurrentOffset = resultsTotalCount - currentOffset;

  yield put(
    refreshTable({
      // Return user to the current page only if the number of deleted items are less than
      // the number of items left from the current offset. This ensures we don't show a blank page.
      preservePage:
        batch?.batchType === BatchType.DELETE
          ? batch?.deviationIds?.length < itemsLeftFromCurrentOffset
          : true,
      // based on action type, preserve the selected rows
      preserveSelection: BATCH_TYPES_THAT_KEEP_SELECTION.includes(
        batch?.batchType
      ),
    })
  );

  if (batch?.batchType === BatchType.PUBLISH) {
    // If any deviations failed to publish, we're never told _which_ failed
    // And the notification only needs to know _how many_ succeeded
    yield put(
      addNotification(NotificationType.DEVIATION_SUBMIT, {
        deviationIds: batch?.deviationIds.slice(
          0,
          processedCount - failedCount
        ),
        isPublish: true,
      })
    );
  }
}

function* handleMergeFlow() {
  const deviationIds = yield select(getSelectedDeviationIds);
  const deviations = yield select(
    getDataEntitiesByIds,
    deviationSchema,
    deviationIds
  );

  // Check if deviations are supported
  const supportedDeviations = deviations.filter((deviation: PapiDeviation) => {
    const deviationType = getDeviationType(deviation);
    const filetype = DeviationMediaService.getMediaFileType(deviation);
    const isMergableFiletype = MULTIIMAGE_ALLOWED_FILETYPES.includes(filetype);
    const onSale = (deviation.onSaleAs || []).length > 0;

    return (
      deviationType === DeviationTypes.IMAGE &&
      isMergableFiletype &&
      !deviation.isMultiImage &&
      !onSale
    );
  });
  const numDeviations = deviations.length;
  const numSupportedDeviations = supportedDeviations.length;
  // In case we don't have at least 2 supported deviations, display the following message
  const numUnsupportedDeviations = numDeviations - numSupportedDeviations;
  if (numSupportedDeviations < 2) {
    yield put(
      pushGlobalConfirmationModal({
        titleTransKey: 'studio.modals.mergeFlow.unableToMergePopup.title',
        textTransKey: 'studio.modals.mergeFlow.unableToMergePopup.body',
        confirmBtnType: 'default',
        confirmBtnLabelTransKey: 'common.gotIt',
        withCancelButton: false,
        icon: <AlertIllustration />,
        translationOptions: {
          count: numUnsupportedDeviations,
          totalCount: deviations.length,
        },
      })
    );
    yield put(batchMergeFailure());
    return;
  }

  // In case not all deviations are supported, but we do have at least 2, display the following message
  if (numUnsupportedDeviations > 0) {
    yield put(
      pushGlobalConfirmationModal({
        titleTransKey:
          'studio.modals.mergeFlow.unsupportedDeviationsPopup.title',
        textTransKey: 'studio.modals.mergeFlow.unsupportedDeviationsPopup.body',
        confirmBtnType: 'default',
        icon: <AlertIllustration />,
        confirmBtnLabelTransKey: 'common.continue',
        withCancelButton: true,
        translationOptions: {
          count: numUnsupportedDeviations,
          totalCount: numDeviations,
        },
      })
    );

    const { no } = yield race({
      yes: take(confirmYes),
      no: take(confirmNo),
    });

    if (no) {
      yield put(batchMergeFailure());
      return;
    }
  }

  // List of deviations includes the main image _and_ attachments
  const currentUserHasCore = yield select(getCurrentUserHasCore);
  if (currentUserHasCore) {
    if (numSupportedDeviations > MAX_ATTACHMENTS_CORE + 1) {
      // Maximum number of attachments reached
      yield put(
        pushGlobalConfirmationModal({
          titleTransKey:
            'deviationSubmit.modals.maximumAttachmentsReached.title',
          textTransKey: 'deviationSubmit.modals.maximumAttachmentsReached.text',
          confirmBtnType: 'default',
          confirmBtnLabelTransKey: 'common.gotIt',
        })
      );
      yield put(batchMergeFailure());
      return;
    }
  } else {
    if (numSupportedDeviations > MAX_ATTACHMENTS + 1) {
      // upgrade to attach more files
      yield put(
        pushModal(ModalType.DEVIATION_SUBMIT_ADDITIONAL_IMAGES_UPSELL, {
          params: {},
        })
      );
      yield put(batchMergeFailure());
      return;
    }
  }

  yield put(
    pushModal(ModalType.DEVIATION_SUBMIT_MAIN_IMAGE_POPUP, {
      params: { deviations: supportedDeviations },
    })
  );
  const { confirm = {} } = yield race({
    confirm: take(confirmValue),
    no: take(confirmNo),
  });

  const { payload: mainDeviation } = confirm;
  if (!mainDeviation) {
    yield put(batchMergeFailure());
    return;
  }

  // confirm merge
  const illustrationStyle = {
    background: 'var(--AS91)',
    width: '140px',
    height: '140px',
    alignSelf: 'flex-start',
  };
  yield put(
    pushGlobalConfirmationModal({
      icon: <div style={illustrationStyle} />,
      titleTransKey: 'studio.modals.mergeFlow.confirmPopup.title',
      textTransKey: 'studio.modals.mergeFlow.confirmPopup.body',
      confirmBtnType: 'default',
      confirmBtnLabelTransKey: 'common.continue',
      withCancelButton: true,
      translationOptions: {
        count: numSupportedDeviations,
        totalCount: numDeviations,
      },
      confirmBtnBiData: BiData<StudioBatchUpdateBiEvent>({
        evid: 12,
        update_type: 'multiimage_merge',
        items_count: numSupportedDeviations,
      }),
    })
  );

  const { no } = yield race({
    yes: take(confirmYes),
    no: take(confirmNo),
  });

  if (no) {
    yield put(batchMergeFailure());
    return;
  }

  const mainDeviationId = mainDeviation.deviationId;
  const mergedDeviationIds = supportedDeviations
    .filter(deviation => deviation.deviationId !== mainDeviationId)
    .map(deviation => deviation.deviationId);

  const batchId = Math.floor(Date.now());
  yield put(batchStart(BatchType.MERGE, batchId, deviationIds));

  // Show the progress modal
  // Since we do not have a way to tell the progress of a merge, we fake it and assume it will last no longer than 6000ms;
  const duration = 6000; // ms
  const now = () => new Date().getTime();
  const startTime = now();
  // getProgress will return 0.0 initially and 1.0 once the duration has elapsed
  // ProgressModal will call the getProgress every pollInterval ms
  const getProgress = () =>
    Math.max(0, Math.min(1, (now() - startTime) / duration));
  const message = i18next.t('studio.modals.progress.text');
  yield put(
    pushModal(ModalType.PROGRESS, {
      params: {
        message,
        getProgress,
        pollInterval: 500,
      },
    })
  );

  // Send merge request
  const response = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'deviation/merge',
      data: {
        main_deviationid: mainDeviationId,
        merged_deviationids: mergedDeviationIds,
      },
    },
    undefined,
    'dashared'
  );

  const runningBatch: RunningBatch = yield select(getRunningBatchById, batchId);
  if (!response || isApiError(response) || !response.success) {
    let errorMessage;
    if (response?.errorCode === UploadErrorCode.UPLOADS_TEMPORARILY_DISABLED) {
      errorMessage = i18next.t('upload.error.temporarilyDisabled');
    } else {
      errorMessage = i18next.t('studio.notifications.batchError.generic.text');
    }
    yield put(
      batchError(BatchType.MERGE, errorMessage, response?.errorCode ?? null)
    );
    yield put(batchMergeFailure());
    yield put(batchFinish(batchId, runningBatch, 0, numDeviations));
    yield put(popModal()); // hide the llama modal
    return;
  }
  yield put(batchMergeSuccess());
  yield put(
    batchFinish(
      batchId,
      runningBatch,
      numSupportedDeviations,
      numUnsupportedDeviations
    )
  );

  // reload the current folder, since its contents changed
  yield put(refreshTable({ preservePage: true }));

  // wait for the reload to finish
  yield race({
    done: take(refreshTableSuccess),
    delay: delay(5000),
  });

  yield put(popModal()); // hide the llama modal

  // show notification that the merge succeeded
  yield put(
    addNotification(NotificationType.DEVIATION_MERGE, {
      deviation: mainDeviation,
      deviationIds: mergedDeviationIds,
    })
  );
}

export default function* batchActionsSaga() {
  yield all([
    takeEvery(batchEditTitle, handleBatchEditTitle),
    takeEvery(batchEditTags, handleBatchEditTags),
    takeEvery(batchEditDescription, handleBatchEditDescription),
    takeEvery(batchEditGallery, handleBatchEditGallery),
    takeEvery(batchEditTier, handleBatchEditTier),
    takeEvery(batchEditLabels, handleBatchEditLabels),
    takeEvery(batchEditLicense, handleBatchEditLicense),
    takeEvery(batchSchedule, handleBatchSchedule),
    takeEvery(batchEditAttribute, handleBatchEditAttribute),
    takeEvery(batchEditDisplayOptions, handleBatchEditDisplayOptions),
    takeEvery(batchEditPreset, handleBatchEditPreset),
    takeEvery(batchPublish, handleBatchPublish),
    takeEvery(batchUnpublish, handleBatchUnpublish),
    takeEvery(batchDoDelete, handleBatchDoDelete),
    takeEvery(batchError, handleBatchError),
    takeEvery(batchFinish, handleBatchFinish),
    takeEvery(batchLoadGalleries, handleBatchLoadGalleries),
    takeEvery(batchLoadTiers, handleBatchLoadTiers),
    takeEvery(batchMerge, handleMergeFlow),
    takeEvery(batchMoveToStash, handleBatchMoveToStash),
    takeEvery(batchMoveToDrafts, handleBatchMoveToDrafts),
    takeEvery(batchDeviationUpdateComplete, handleBatchDeviationUpdateComplete),
    takeEvery(batchStart, closeModalIfMobile),
  ]);
}
